import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdateHome() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    accessFacilitiesStatus: '',
    currentAccessStandard: '',
    accessAreasNeeded: [],
    necessaryFacilities: [],
    otherFacility: '',
    hasToilet: '',
    toiletType: '',
    toiletSuitable: '',
    toiletChangesNeeded: [],
    otherToiletChange: '',
    noToiletReason: '',
    otherNoToiletReason: '',
    suitableToiletType: '',
    toiletSetupChanges: [],
    otherSetupChange: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message: 'Missing required parameters.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getContributionAtHomeDetails(
        recordId,
        sheetId
      );

      if (!response.success) {
        setAlert({
          show: true,
          message:
            response.message || 'Failed to load home contribution details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      // Parse arrays and handle "Other:" prefix
      const parseArrayWithOther = (str, otherFieldName) => {
        if (!str) return { array: [], otherValue: '' };
        const items = str
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s);
        const otherItem = items.find((item) => item.startsWith('Other:'));
        if (otherItem) {
          const otherValue = otherItem.replace('Other:', '').trim();
          const array = items
            .filter((item) => !item.startsWith('Other:'))
            .concat(['Other']);
          return { array, otherValue };
        }
        return { array: items, otherValue: '' };
      };

      const accessAreas = parseArrayWithOther(
        response.record.accessAreasNeeded
      );
      const facilities = parseArrayWithOther(
        response.record.necessaryFacilities
      );
      const toiletChanges = parseArrayWithOther(
        response.record.toiletChangesNeeded
      );
      const setupChanges = parseArrayWithOther(
        response.record.toiletSetupChanges
      );

      // Handle noToiletReason "Other:" prefix
      let noToiletReason = response.record.noToiletReason || '';
      let otherNoToiletReason = '';
      if (noToiletReason.startsWith('Other:')) {
        otherNoToiletReason = noToiletReason.replace('Other:', '').trim();
        noToiletReason = 'Other';
      }

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        accessFacilitiesStatus: response.record.accessFacilitiesStatus || '',
        currentAccessStandard: response.record.currentAccessStandard || '',
        accessAreasNeeded: accessAreas.array,
        necessaryFacilities: facilities.array,
        otherFacility: facilities.otherValue,
        hasToilet: response.record.hasToilet || '',
        toiletType: response.record.toiletType || '',
        toiletSuitable: response.record.toiletSuitable || '',
        toiletChangesNeeded: toiletChanges.array,
        otherToiletChange: toiletChanges.otherValue,
        noToiletReason: noToiletReason,
        otherNoToiletReason: otherNoToiletReason,
        suitableToiletType: response.record.suitableToiletType || '',
        toiletSetupChanges: setupChanges.array,
        otherSetupChange: setupChanges.otherValue,
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updates = { [name]: value };

    // Handle access facilities status change
    if (name === 'accessFacilitiesStatus') {
      if (value !== 'Currently set up') {
        updates.currentAccessStandard = '';
      }
      if (value !== 'Required') {
        updates.accessAreasNeeded = [];
        updates.necessaryFacilities = [];
        updates.otherFacility = '';
      }
    }

    // Handle has toilet change
    if (name === 'hasToilet') {
      if (value !== 'Yes') {
        updates.toiletType = '';
        updates.toiletSuitable = '';
        updates.toiletChangesNeeded = [];
        updates.otherToiletChange = '';
      }
      if (value !== 'No') {
        updates.noToiletReason = '';
        updates.otherNoToiletReason = '';
        updates.suitableToiletType = '';
        updates.toiletSetupChanges = [];
        updates.otherSetupChange = '';
      }
    }

    // Handle toilet suitable change
    if (name === 'toiletSuitable' && value !== 'No') {
      updates.toiletChangesNeeded = [];
      updates.otherToiletChange = '';
    }

    // Handle no toilet reason change
    if (name === 'noToiletReason' && value !== 'Other') {
      updates.otherNoToiletReason = '';
    }

    setFormData({
      ...formData,
      ...updates,
    });
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData((prev) => {
      const currentArray = prev[fieldName];
      const newArray = checked
        ? [...currentArray, value]
        : currentArray.filter((item) => item !== value);

      const updates = { [fieldName]: newArray };

      // Clear "other" field if "Other" checkbox is unchecked
      if (!checked && value === 'Other') {
        if (fieldName === 'necessaryFacilities') updates.otherFacility = '';
        if (fieldName === 'toiletChangesNeeded') updates.otherToiletChange = '';
        if (fieldName === 'toiletSetupChanges') updates.otherSetupChange = '';
      }

      return { ...prev, ...updates };
    });
  };

  const shouldShowCurrentAccessStandard = () => {
    return formData.accessFacilitiesStatus === 'Currently set up';
  };

  const shouldShowRequiredAccess = () => {
    return formData.accessFacilitiesStatus === 'Required';
  };

  const shouldShowNecessaryFacilities = () => {
    return formData.accessAreasNeeded.length > 0;
  };

  const shouldShowOtherFacility = () => {
    return formData.necessaryFacilities.includes('Other');
  };

  const shouldShowHasToiletSection = () => {
    return formData.hasToilet === 'Yes';
  };

  const shouldShowNoToiletSection = () => {
    return formData.hasToilet === 'No';
  };

  const shouldShowToiletChanges = () => {
    return formData.toiletSuitable === 'No';
  };

  const shouldShowOtherToiletChange = () => {
    return formData.toiletChangesNeeded.includes('Other');
  };

  const shouldShowOtherSetupChange = () => {
    return formData.toiletSetupChanges.includes('Other');
  };

  const hasChanges = () => {
    const arrayToString = (arr) => arr.sort().join(', ');

    const current = {
      ...formData,
      accessAreasNeeded: arrayToString(formData.accessAreasNeeded),
      necessaryFacilities: arrayToString(formData.necessaryFacilities),
      toiletChangesNeeded: arrayToString(formData.toiletChangesNeeded),
      toiletSetupChanges: arrayToString(formData.toiletSetupChanges),
    };

    const original = {
      ...originalData,
      accessAreasNeeded: arrayToString(originalData.accessAreasNeeded),
      necessaryFacilities: arrayToString(originalData.necessaryFacilities),
      toiletChangesNeeded: arrayToString(originalData.toiletChangesNeeded),
      toiletSetupChanges: arrayToString(originalData.toiletSetupChanges),
    };

    return JSON.stringify(current) !== JSON.stringify(original);
  };

  const arrayToStringWithOther = (arr, otherValue) => {
    const filtered = arr.filter((item) => item !== 'Other');
    if (arr.includes('Other') && otherValue) {
      filtered.push(`Other: ${otherValue}`);
    }
    return filtered.join(', ');
  };

  const handleSave = async () => {
    // Validation
    if (!formData.accessFacilitiesStatus || !formData.hasToilet) {
      setAlert({
        show: true,
        message: 'Please fill in all required fields.',
        type: 'warning',
      });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      // Build noToiletReason with "Other:" prefix if needed
      let noToiletReasonFinal = formData.noToiletReason;
      if (formData.noToiletReason === 'Other' && formData.otherNoToiletReason) {
        noToiletReasonFinal = `Other: ${formData.otherNoToiletReason}`;
      }

      const updateData = {
        uniqueId: recordData.uniqueId,
        accessFacilitiesStatus: formData.accessFacilitiesStatus,
        currentAccessStandard:
          formData.accessFacilitiesStatus === 'Currently set up'
            ? formData.currentAccessStandard
            : '',
        accessAreasNeeded:
          formData.accessFacilitiesStatus === 'Required'
            ? formData.accessAreasNeeded.join(', ')
            : '',
        necessaryFacilities:
          formData.accessFacilitiesStatus === 'Required'
            ? arrayToStringWithOther(
                formData.necessaryFacilities,
                formData.otherFacility
              )
            : '',
        hasToilet: formData.hasToilet,
        toiletType: formData.hasToilet === 'Yes' ? formData.toiletType : '',
        toiletSuitable:
          formData.hasToilet === 'Yes' ? formData.toiletSuitable : '',
        toiletChangesNeeded:
          formData.hasToilet === 'Yes' && formData.toiletSuitable === 'No'
            ? arrayToStringWithOther(
                formData.toiletChangesNeeded,
                formData.otherToiletChange
              )
            : '',
        noToiletReason: formData.hasToilet === 'No' ? noToiletReasonFinal : '',
        suitableToiletType:
          formData.hasToilet === 'No' ? formData.suitableToiletType : '',
        toiletSetupChanges:
          formData.hasToilet === 'No'
            ? arrayToStringWithOther(
                formData.toiletSetupChanges,
                formData.otherSetupChange
              )
            : '',
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updateContributionAtHomeDetails(
        updateData
      );

      if (response.success) {
        setAlert({
          show: true,
          message: 'Home contribution details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/welfare/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/assistance/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={10}
        title="Update Contribution at Home Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading home contribution details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={10}
        title="Update Contribution at Home Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load home contribution details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={10}
      title="Update Contribution at Home Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">
          In-house Access / Access Facilities
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            In-house access / access facilities status{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            name="accessFacilitiesStatus"
            value={formData.accessFacilitiesStatus}
            onChange={handleInputChange}
            required
            disabled={!canEdit}
          >
            <option value="">Select...</option>
            <option value="Currently set up">Currently set up</option>
            <option value="Required">Required</option>
            <option value="Not required">Not required</option>
          </Form.Select>
        </Form.Group>

        {shouldShowCurrentAccessStandard() && (
          <Form.Group className="mb-3 conditional-field">
            <Form.Label>Status / Standard of current access</Form.Label>
            <Form.Select
              name="currentAccessStandard"
              value={formData.currentAccessStandard}
              onChange={handleInputChange}
              disabled={!canEdit}
            >
              <option value="">Select...</option>
              {legendData?.accessStandard?.map((item, index) => (
                <option key={index} value={item}>
                  {item}
                </option>
              ))}
            </Form.Select>
            <span className="info-badge">For currently set up facilities</span>
          </Form.Group>
        )}

        {shouldShowRequiredAccess() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>
                Access / Support Areas needed (Select all that apply)
              </Form.Label>
              <div className="checkbox-group">
                {legendData?.accessAreas?.map((item, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`accessArea-${index}`}
                    label={item}
                    checked={formData.accessAreasNeeded.includes(item)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        'accessAreasNeeded',
                        item,
                        e.target.checked
                      )
                    }
                    disabled={!canEdit}
                  />
                ))}
              </div>
              <span className="info-badge">For required facilities</span>
            </Form.Group>

            {shouldShowNecessaryFacilities() && (
              <>
                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Necessary facilities (Select all that apply)
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.necessaryFacilities?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`facility-${index}`}
                        label={item}
                        checked={formData.necessaryFacilities.includes(item)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            'necessaryFacilities',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                </Form.Group>

                {shouldShowOtherFacility() && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>Please specify other facility</Form.Label>
                    <Form.Control
                      type="text"
                      name="otherFacility"
                      value={formData.otherFacility}
                      onChange={handleInputChange}
                      placeholder="Enter other facility type"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                )}
              </>
            )}
          </div>
        )}

        <div className="section-header">Toilet Facilities</div>

        <Form.Group className="mb-3">
          <Form.Label>
            Do you have toilet facilities?{' '}
            <span className="text-danger">*</span>
          </Form.Label>
          <div>
            <Form.Check
              type="radio"
              label="Yes"
              name="hasToilet"
              value="Yes"
              checked={formData.hasToilet === 'Yes'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
            <Form.Check
              type="radio"
              label="No"
              name="hasToilet"
              value="No"
              checked={formData.hasToilet === 'No'}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </div>
        </Form.Group>

        {shouldShowHasToiletSection() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>Type of toilet</Form.Label>
              <Form.Select
                name="toiletType"
                value={formData.toiletType}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {legendData?.toiletType?.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Is the toilet suitable for the disabled?</Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="toiletSuitable"
                  value="Yes"
                  checked={formData.toiletSuitable === 'Yes'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="toiletSuitable"
                  value="No"
                  checked={formData.toiletSuitable === 'No'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
              </div>
            </Form.Group>

            {shouldShowToiletChanges() && (
              <>
                <Form.Group className="mb-3 conditional-field">
                  <Form.Label>
                    Changes to be made to toilet (Select all that apply)
                  </Form.Label>
                  <div className="checkbox-group">
                    {legendData?.toiletChanges?.map((item, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        id={`toiletChange-${index}`}
                        label={item}
                        checked={formData.toiletChangesNeeded.includes(item)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            'toiletChangesNeeded',
                            item,
                            e.target.checked
                          )
                        }
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                  <span className="info-badge">For unsuitable toilets</span>
                </Form.Group>

                {shouldShowOtherToiletChange() && (
                  <Form.Group className="mb-3 conditional-field">
                    <Form.Label>Please specify other change</Form.Label>
                    <Form.Control
                      type="text"
                      name="otherToiletChange"
                      value={formData.otherToiletChange}
                      onChange={handleInputChange}
                      placeholder="Enter other change needed"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                )}
              </>
            )}
          </div>
        )}

        {shouldShowNoToiletSection() && (
          <div className="conditional-field">
            <Form.Group className="mb-3">
              <Form.Label>Reason for not having toilet</Form.Label>
              <SelectWithOther
                name="noToiletReason"
                value={formData.noToiletReason}
                otherName="otherNoToiletReason"
                otherValue={formData.otherNoToiletReason}
                options={legendData?.noToiletReason || []}
                onChange={handleInputChange}
                disabled={!canEdit}
                otherPlaceholder="Please specify other reason"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type of toilet suitable for the disabled</Form.Label>
              <Form.Select
                name="suitableToiletType"
                value={formData.suitableToiletType}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {legendData?.suitableToiletType?.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Necessary changes for toilet setup (Select all that apply)
              </Form.Label>
              <div className="checkbox-group">
                {legendData?.toiletSetupChanges?.map((item, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`setupChange-${index}`}
                    label={item}
                    checked={formData.toiletSetupChanges.includes(item)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        'toiletSetupChanges',
                        item,
                        e.target.checked
                      )
                    }
                    disabled={!canEdit}
                  />
                ))}
              </div>
            </Form.Group>

            {shouldShowOtherSetupChange() && (
              <Form.Group className="mb-3 conditional-field">
                <Form.Label>Please specify other change</Form.Label>
                <Form.Control
                  type="text"
                  name="otherSetupChange"
                  value={formData.otherSetupChange}
                  onChange={handleInputChange}
                  placeholder="Enter other change needed"
                  disabled={!canEdit}
                />
              </Form.Group>
            )}
          </div>
        )}
      </Form>
    </UpdateLayout>
  );
}

export default UpdateHome;
